import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { RegionalHubLinks } from "@/components/RegionalHubLinks";
import { seoHead } from "@/components/hubs/HubPages";
import { getIndustryTips, TIP_TOPICS } from "@/lib/industryTips";
import { getCaseStudy } from "@/lib/caseStudiesData";

export const Route = createFileRoute("/learn/tips/$slug")({
  loader: ({ params }) => {
    const data = getIndustryTips(params.slug);
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData, params }) =>
    loaderData
      ? seoHead(`/learn/tips/${params.slug}`, loaderData.metaTitle, loaderData.desc, "article")
      : { meta: [{ title: "Guide not found" }, { name: "robots", content: "noindex" }] },
  notFoundComponent: () => (
    <div className="p-10 text-center">
      Guide not found. <Link to="/learn">Back to the Learning Hub</Link>
    </div>
  ),
  component: TipsPage,
});

function TipsPage() {
  const g = Route.useLoaderData();
  const path = `/learn/tips/${g.slug}`;
  const studies = (g.caseStudySlugs ?? [])
    .map(getCaseStudy)
    .filter((s): s is NonNullable<typeof s> => Boolean(s));
  return (
    <ArticleLayout
      eyebrow={`${g.industry} · Marketing Tips`}
      read="6 min read"
      title={g.title}
      intro={g.intro}
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Learning Hub", to: "/learn" }, { label: g.industry }]}
      path={path}
      faqs={g.faqs}
    >
      {TIP_TOPICS.map((t) => (
        <div key={t}>
          <h2>{t} tips for {g.industry.toLowerCase()}</h2>
          <ul>{g.tips[t].map((tip) => <li key={tip}>{tip}</li>)}</ul>
        </div>
      ))}
      <h2>Good vs. bad marketing</h2>
      <p><strong>What works:</strong></p>
      <ul>{g.good.map((x) => <li key={x}>{x}</li>)}</ul>
      <p><strong>What to avoid:</strong></p>
      <ul>{g.bad.map((x) => <li key={x}>{x}</li>)}</ul>
      <p>
        Start with the free channels you own — your website, Google Maps, search, AI answers, and social media —
        before spending on paid platforms.
      </p>

      {g.plan && (
        <>
          <h2>Your 90-day local SEO plan</h2>
          <div className="not-prose grid gap-4 md:grid-cols-3">
            {g.plan.map((p) => (
              <div key={p.when} className="rounded-2xl border border-border bg-card p-5 shadow-card">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-gold-ink">{p.when}</p>
                <h3 className="mt-1 text-lg font-bold text-foreground">{p.title}</h3>
                <ul className="mt-3 space-y-2 text-sm text-foreground/85">
                  {p.steps.map((s) => <li key={s}>✓ {s}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}

      {studies.length > 0 && (
        <>
          <h2>Real Colorado results</h2>
          {g.caseStudyNote && <p>{g.caseStudyNote}</p>}
          <div className={`not-prose grid gap-4 ${studies.length === 1 ? "" : "md:grid-cols-2"}`}>
            {studies.map((c) => (
              <Link
                key={c.slug}
                to="/case-studies/$studySlug"
                params={{ studySlug: c.slug }}
                className="block rounded-2xl border border-border bg-card p-5 shadow-card transition-smooth hover:border-brand-gold"
              >
                <p className="text-xs font-bold uppercase tracking-wider text-brand-gold-ink">{c.industry} · {c.location}</p>
                <p className="mt-2 text-xl font-extrabold text-foreground">{c.heroResult}</p>
                <p className="mt-2 text-sm text-muted-foreground">{c.summary}</p>
                <p className="mt-3 text-sm font-semibold text-foreground">Read the case study →</p>
              </Link>
            ))}
          </div>
          <p className="text-xs">Results are anonymized for privacy and don&apos;t guarantee future results.</p>
        </>
      )}

      <div className="not-prose my-10 rounded-2xl border border-brand-gold/40 bg-card p-6 shadow-card sm:p-8">
        <h2 className="text-xl font-extrabold text-foreground sm:text-2xl">Curious how your business stacks up?</h2>
        <p className="mt-2 text-muted-foreground">
          We help Colorado businesses get found online. No pressure — just a friendly look at what could work for you.
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <Link
            to="/free-mockup"
            className="inline-flex items-center justify-center rounded-full bg-brand-gold-deep px-5 py-3 text-sm font-bold text-background transition-spring hover:opacity-90"
          >
            Get a Free Website Mockup
          </Link>
          <Link
            to="/free-audit"
            className="inline-flex items-center justify-center rounded-full border border-border bg-background px-5 py-3 text-sm font-bold text-foreground transition-spring hover:border-brand-gold"
          >
            Get a Free Marketing Audit
          </Link>
        </div>
      </div>

      <RegionalHubLinks excludePath={path} />
    </ArticleLayout>
  );
}
